// HubPlusReminderBanner — dismissible countdown/reminder banner shown
// when the franchisee's +HUB complimentary window is within 7 days of
// ending. Backed by GET /api/hub-plus/me. Dismissal is per-calendar-day
// (stored in sessionStorage as an ISO YYYY-MM-DD stamp): if the
// franchisee dismisses today, the banner stays hidden for the rest of
// today; the next calendar day it reappears (assuming they're still
// inside the 7-day warning window). Prior behaviour was a 24h auto-
// clear which could suppress the banner across most of the warning
// window for a franchisee who revisited daily inside the same tab
// session — see Joanne Goodrum (#0082) 2026-02 diagnostic.
//
// Deliberately conservative in scope:
//   * No purchase flow.
//   * No email/notifications tie-in.
//   * No admin config surface (7-day window is hard-coded to match
//     the backend logic in hub_plus_cohort_routes.py hub_plus/me).
//
// Rendered inside PortalShell (below <PortalNewVersionBanner />) so it
// sits above the sticky top bar on every /portal/* route.
import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import api from "@/lib/api";

const DISMISS_KEY = "hubPlusReminderDismissedOn";

// Local calendar-day key in ISO YYYY-MM-DD. Uses the browser's locale
// so a franchisee based in the UK gets UK-local "today" — matching
// their intuition when they dismiss "today". Backend days_remaining
// is still computed in UTC (unchanged).
function todayKey() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function HubPlusReminderBanner() {
  const [state, setState] = useState({ loading: true, show: false, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/hub-plus/me");
        if (cancelled) return;
        const dismissedOn = sessionStorage.getItem(DISMISS_KEY);
        // Dismissal is scoped to the calendar day. If the stored day
        // isn't today, treat it as expired and clear it so a fresh
        // dismissal captures the new day cleanly.
        const today = todayKey();
        if (dismissedOn && dismissedOn !== today) {
          sessionStorage.removeItem(DISMISS_KEY);
        }
        const dismissed = dismissedOn === today;
        setState({
          loading: false,
          show: Boolean(data?.show_reminder_banner) && !dismissed,
          data,
        });
      } catch (err) {
        // Fail silent — the banner is a nice-to-have; the portal
        // must never break because /hub-plus/me hiccups.
        if (!cancelled) setState({ loading: false, show: false, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading || !state.show || !state.data) return null;

  const { days_remaining, complimentary_ends_at } = state.data;
  const endsFormatted = complimentary_ends_at
    ? new Date(complimentary_ends_at).toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  const daysLabel = (() => {
    if (days_remaining == null) return "shortly";
    if (days_remaining <= 0) return "today";
    if (days_remaining === 1) return "tomorrow";
    return `in ${days_remaining} days`;
  })();

  const onDismiss = () => {
    // Store today's YYYY-MM-DD so the banner stays hidden for the
    // rest of today and reappears on the next calendar day.
    sessionStorage.setItem(DISMISS_KEY, todayKey());
    setState((s) => ({ ...s, show: false }));
  };

  return (
    <div
      role="alert"
      data-testid="hub-plus-reminder-banner"
      className="bg-amber-50 border-b border-amber-300 text-amber-900"
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2.5 flex items-start sm:items-center gap-3">
        <AlertTriangle
          className="h-5 w-5 shrink-0 mt-0.5 sm:mt-0 text-amber-700"
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0 text-sm leading-snug">
          <span className="font-semibold" data-testid="hub-plus-reminder-headline">
            Your +HUB complimentary access ends {daysLabel}
            {endsFormatted ? ` — ${endsFormatted}` : ""}.
          </span>{" "}
          <span
            className="text-amber-800/90"
            data-testid="hub-plus-reminder-subcopy"
          >
            You&apos;ll still keep the standard portal after that; +HUB add-on
            modules (My Territory+, Marketing+, Invoicing+) will
            pause unless you pick a paid bundle.
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-2 shrink-0 p-1 rounded hover:bg-amber-100 transition-colors"
          aria-label="Dismiss reminder"
          data-testid="hub-plus-reminder-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
